// @ts-nocheck
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface UserPreferences {
  dismissedModules: Record<string, boolean>;
  calendarSyncTrackerTasks?: boolean;
  calendarSyncSavedColleges?: boolean;
  calendarSyncCollegeCamps?: boolean;
}

export function useUserPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>({
    dismissedModules: {}
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Try to get existing preferences - use maybeSingle() to handle missing rows gracefully
        const { data, error } = await supabase
          .from("user_preferences")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (error) {
          console.error("Error fetching user preferences:", error);
          return;
        }
        
        if (data) {
          setPreferences({
            dismissedModules: data.dismissed_modules || {},
            calendarSyncTrackerTasks: data.calendar_sync_tracker_tasks ?? true,
            calendarSyncSavedColleges: data.calendar_sync_saved_colleges ?? true,
            calendarSyncCollegeCamps: data.calendar_sync_college_camps ?? true
          });
        }
      } catch (error) {
        console.error("Error in user preferences hook:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPreferences();
  }, [user?.id]);

  // Update a module's dismissed status
  const dismissModule = async (moduleId: string) => {
    if (!user?.id) return;
    
    // Update local state immediately for responsive UI
    setPreferences(prev => ({
      ...prev,
      dismissedModules: {
        ...prev.dismissedModules,
        [moduleId]: true
      }
    }));

    try {
      // Check if preferences record exists
      const { data: existingData } = await supabase
        .from("user_preferences")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (existingData) {
        // Update existing record
        await supabase
          .from("user_preferences")
          .update({
            dismissed_modules: {
              ...preferences.dismissedModules,
              [moduleId]: true
            },
            updated_at: new Date().toISOString()
          })
          .eq("user_id", user.id);
      } else {
        // Create new record
        await supabase
          .from("user_preferences")
          .insert({
            user_id: user.id,
            dismissed_modules: { [moduleId]: true }
          });
      }
    } catch (error) {
      console.error("Error updating user preferences:", error);
      
      // Revert local state if server update fails
      setPreferences(prev => ({
        ...prev,
        dismissedModules: {
          ...prev.dismissedModules,
          [moduleId]: prev.dismissedModules[moduleId] || false
        }
      }));
    }
  };

  // Un-dismiss a module (show instructions again)
  const undismissModule = async (moduleId: string) => {
    if (!user?.id) return;
    
    // Update local state immediately for responsive UI
    const newDismissedModules = { ...preferences.dismissedModules };
    delete newDismissedModules[moduleId];
    
    setPreferences(prev => ({
      ...prev,
      dismissedModules: newDismissedModules
    }));

    try {
      const { data: existingData } = await supabase
        .from("user_preferences")
        .select("id, dismissed_modules")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (existingData) {
        const updatedModules = { ...(existingData.dismissed_modules || {}) };
        delete updatedModules[moduleId];
        
        await supabase
          .from("user_preferences")
          .update({
            dismissed_modules: updatedModules,
            updated_at: new Date().toISOString()
          })
          .eq("user_id", user.id);
      }
    } catch (error) {
      console.error("Error undismissing module:", error);
    }
  };

  // Check if a module has been dismissed
  const isModuleDismissed = (moduleId: string) => {
    return !!preferences.dismissedModules?.[moduleId];
  };

  // Update calendar sync preferences
  const updateCalendarSyncPreferences = async (preferences: {
    trackerTasks?: boolean;
    savedColleges?: boolean;
    collegeCamps?: boolean;
  }) => {
    if (!user?.id) return false;
    
    // Update local state immediately for responsive UI
    setPreferences(prev => ({
      ...prev,
      calendarSyncTrackerTasks: 
        preferences.trackerTasks !== undefined 
          ? preferences.trackerTasks 
          : prev.calendarSyncTrackerTasks,
      calendarSyncSavedColleges: 
        preferences.savedColleges !== undefined 
          ? preferences.savedColleges 
          : prev.calendarSyncSavedColleges,
      calendarSyncCollegeCamps: 
        preferences.collegeCamps !== undefined 
          ? preferences.collegeCamps 
          : prev.calendarSyncCollegeCamps
    }));

    try {
      // Check if preferences record exists - use maybeSingle() here too
      const { data: existingData } = await supabase
        .from("user_preferences")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();
      
      const updates = {
        ...(preferences.trackerTasks !== undefined && { calendar_sync_tracker_tasks: preferences.trackerTasks }),
        ...(preferences.savedColleges !== undefined && { calendar_sync_saved_colleges: preferences.savedColleges }),
        ...(preferences.collegeCamps !== undefined && { calendar_sync_college_camps: preferences.collegeCamps }),
        updated_at: new Date().toISOString()
      };
      
      if (existingData) {
        // Update existing record
        await supabase
          .from("user_preferences")
          .update(updates)
          .eq("user_id", user.id);
      } else {
        // Create new record
        await supabase
          .from("user_preferences")
          .insert({
            user_id: user.id,
            dismissed_modules: {},
            ...updates
          });
      }
      
      return true;
    } catch (error) {
      console.error("Error updating calendar sync preferences:", error);
      return false;
    }
  };

  return {
    preferences,
    isLoading,
    dismissModule,
    undismissModule,
    isModuleDismissed,
    updateCalendarSyncPreferences
  };
}
